// src/pages/finance-portal/cash-flow-calendar/CashFlowCalendar.js
//
// Cash Flow Calendar
//
// Day-grid view of projected inflows / outflows + running balance, with
// drill-down on any day to the underlying events. Manual events can be
// added to model expected wires, capital injections, etc.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
    DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
    MenuItem, Skeleton, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';
const POSITIVE = '#10b981';
const NEGATIVE = '#ef4444';

const SOURCE_LABELS = {
    AR_INVOICE: 'AR Invoice',
    AP_BILL: 'AP Bill',
    PAYROLL: 'Payroll',
    LOAN_PAYMENT: 'Loan',
    RECURRING: 'Recurring',
    MANUAL: 'Manual',
};

function formatCurrency(amount) {
    if (amount == null || Number.isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatCompact(amount) {
    if (amount == null || Number.isNaN(amount)) return '$0';
    const abs = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
}

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildCalendarGrid(monthStart, days) {
    // Build a 6x7 grid keyed by ISO date
    const dayMap = {};
    days.forEach(d => { dayMap[d.date] = d; });

    const firstDow = monthStart.getDay();
    const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
        const dt = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
        const iso = dt.toISOString().slice(0, 10);
        cells.push({ date: iso, day, payload: dayMap[iso] });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

const EMPTY_EVENT = {
    event_date: '',
    direction: 'INFLOW',
    amount: '',
    description: '',
    counterparty: '',
};

export default function CashFlowCalendar() {
    const [horizon, setHorizon] = useState(60);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [error, setError] = useState(null);
    const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
    const [selectedDay, setSelectedDay] = useState(null);
    const [eventDialog, setEventDialog] = useState({ open: false, form: { ...EMPTY_EVENT } });

    const fetchCalendar = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/cash-position/calendar', {
                params: { horizon_days: horizon },
            });
            if (res.data?.success) setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load calendar.');
        } finally {
            setIsLoading(false);
        }
    }, [horizon]);

    useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        try {
            await axiosInstance.post('/api/finance/cash-position/events/regenerate', { horizon_days: horizon });
            await fetchCalendar();
        } catch (err) {
            setError(err.response?.data?.message || 'Regeneration failed.');
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleSaveEvent = async () => {
        const { form } = eventDialog;
        try {
            await axiosInstance.post('/api/finance/cash-position/events', form);
            setEventDialog({ open: false, form: { ...EMPTY_EVENT } });
            await fetchCalendar();
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save event.');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            await axiosInstance.delete(`/api/finance/cash-position/events/${eventId}`);
            await fetchCalendar();
            if (selectedDay) {
                setSelectedDay({
                    ...selectedDay,
                    events: (selectedDay.events || []).filter(e => e.id !== eventId),
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const grid = useMemo(() => buildCalendarGrid(monthCursor, data?.days || []), [monthCursor, data]);

    const monthLabel = monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const monthAggregates = useMemo(() => {
        if (!data?.days) return null;
        const monthStr = monthCursor.toISOString().slice(0, 7);
        const inMonth = data.days.filter(d => d.date.startsWith(monthStr));
        const inflow = inMonth.reduce((a, d) => a + (d.inflow || 0), 0);
        const outflow = inMonth.reduce((a, d) => a + (d.outflow || 0), 0);
        const net = inflow - outflow;
        return { inflow, outflow, net, count: inMonth.length };
    }, [data, monthCursor]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Cash Flow Calendar
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Day-by-day projected inflows, outflows, and running cash position.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <ToggleButtonGroup
                        value={horizon}
                        exclusive
                        size="small"
                        onChange={(_, v) => v && setHorizon(v)}
                        sx={{ '& .MuiToggleButton-root': { fontFamily, textTransform: 'none', px: 2 } }}
                    >
                        <ToggleButton value={30}>30D</ToggleButton>
                        <ToggleButton value={60}>60D</ToggleButton>
                        <ToggleButton value={90}>90D</ToggleButton>
                        <ToggleButton value={180}>6M</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="outlined"
                        startIcon={isRegenerating ? <CircularProgress size={14} /> : <AutorenewIcon />}
                        onClick={handleRegenerate}
                        disabled={isRegenerating}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Regenerate
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setEventDialog({ open: true, form: { ...EMPTY_EVENT } })}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                    >
                        Add Event
                    </Button>
                    <IconButton onClick={fetchCalendar}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Summary cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                Starting Balance
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#0f172a', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(data?.starting_balance)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                Projected Ending
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#0f172a', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(data?.ending_projected_balance)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <WaterDropIcon sx={{ color: NEGATIVE, fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                    Low Water Mark
                                </Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: NEGATIVE, mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(data?.low_water_mark?.projected_balance)}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                {data?.low_water_mark?.date || '—'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                Net {horizon}D Change
                            </Typography>
                            <Typography sx={{
                                fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5,
                                color: (data?.net_change || 0) >= 0 ? POSITIVE : NEGATIVE,
                            }}>
                                {isLoading ? <Skeleton width={120} /> :
                                    `${(data?.net_change || 0) >= 0 ? '+' : ''}${formatCurrency(data?.net_change)}`}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                {formatCurrency(data?.total_inflow)} in / {formatCurrency(data?.total_outflow)} out
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Calendar grid */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}>
                                <ArrowUpwardIcon sx={{ transform: 'rotate(-90deg)' }} />
                            </IconButton>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', minWidth: 160, textAlign: 'center' }}>
                                {monthLabel}
                            </Typography>
                            <IconButton onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}>
                                <ArrowDownwardIcon sx={{ transform: 'rotate(-90deg)' }} />
                            </IconButton>
                        </Stack>
                        {monthAggregates && (
                            <Stack direction="row" spacing={2}>
                                <Chip
                                    icon={<TrendingUpIcon sx={{ color: `${POSITIVE} !important` }} />}
                                    label={`In ${formatCurrency(monthAggregates.inflow)}`}
                                    sx={{ fontFamily, bgcolor: '#dcfce7', color: '#065f46', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<TrendingDownIcon sx={{ color: `${NEGATIVE} !important` }} />}
                                    label={`Out ${formatCurrency(monthAggregates.outflow)}`}
                                    sx={{ fontFamily, bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }}
                                />
                                <Chip
                                    label={`Net ${formatCurrency(monthAggregates.net)}`}
                                    sx={{
                                        fontFamily, fontWeight: 600,
                                        bgcolor: monthAggregates.net >= 0 ? '#dbeafe' : '#fef3c7',
                                        color: monthAggregates.net >= 0 ? '#1e40af' : '#92400e',
                                    }}
                                />
                            </Stack>
                        )}
                    </Box>

                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={400} /></Box>
                    ) : (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                                    <Typography key={d} sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textAlign: 'center', py: 1 }}>
                                        {d}
                                    </Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                                {grid.map((cell, idx) => {
                                    if (!cell) {
                                        return <Box key={idx} sx={{ minHeight: 84, borderRadius: '8px', bgcolor: '#f8fafc', opacity: 0.4 }} />;
                                    }
                                    const p = cell.payload;
                                    const net = p?.net || 0;
                                    const today = new Date().toISOString().slice(0, 10) === cell.date;
                                    return (
                                        <Tooltip
                                            key={cell.date}
                                            title={p
                                                ? `Net ${formatCurrency(net)} • Bal ${formatCurrency(p.projected_balance)}`
                                                : 'No events'
                                            }
                                        >
                                            <Box
                                                onClick={() => p && setSelectedDay(p)}
                                                sx={{
                                                    minHeight: 84, p: 1, borderRadius: '8px', cursor: p?.event_count ? 'pointer' : 'default',
                                                    border: today ? `2px solid ${ACCENT}` : '1px solid #e2e8f0',
                                                    bgcolor: net > 0 ? 'rgba(16, 185, 129, 0.08)' :
                                                        net < 0 ? 'rgba(239, 68, 68, 0.08)' :
                                                            '#fff',
                                                    transition: 'all 0.15s',
                                                    '&:hover': p?.event_count ? { borderColor: ACCENT, transform: 'scale(1.02)' } : {},
                                                }}
                                            >
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: today ? ACCENT : '#475569', fontWeight: 700 }}>
                                                    {cell.day}
                                                </Typography>
                                                {p && p.event_count > 0 && (
                                                    <>
                                                        {p.inflow > 0 && (
                                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: POSITIVE, fontWeight: 600, mt: 0.5 }}>
                                                                +{formatCompact(p.inflow)}
                                                            </Typography>
                                                        )}
                                                        {p.outflow > 0 && (
                                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: NEGATIVE, fontWeight: 600 }}>
                                                                -{formatCompact(p.outflow)}
                                                            </Typography>
                                                        )}
                                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8', mt: 0.5 }}>
                                                            {p.event_count} item{p.event_count > 1 ? 's' : ''}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Day drill-down dialog */}
            <Dialog open={!!selectedDay} onClose={() => setSelectedDay(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {selectedDay?.date}
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400, mt: 0.5 }}>
                        Net {formatCurrency(selectedDay?.net)} • Projected balance {formatCurrency(selectedDay?.projected_balance)}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {(!selectedDay?.events || selectedDay.events.length === 0) ? (
                        <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 3 }}>
                            No events for this day.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569' } }}>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Counterparty</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedDay.events.map((ev) => (
                                    <TableRow key={ev.id}>
                                        <TableCell sx={{ fontFamily }}>
                                            <Chip
                                                label={SOURCE_LABELS[ev.source] || ev.source}
                                                size="small"
                                                sx={{ fontFamily, fontSize: '0.7rem', bgcolor: '#f1f5f9' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }}>{ev.description || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily }}>{ev.counterparty || '—'}</TableCell>
                                        <TableCell align="right" sx={{
                                            fontFamily, fontWeight: 700,
                                            color: ev.direction === 'INFLOW' ? POSITIVE : NEGATIVE,
                                        }}>
                                            {ev.direction === 'INFLOW' ? '+' : '-'}{formatCurrency(ev.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={ev.status} size="small" sx={{ fontFamily, fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            {ev.source === 'MANUAL' && (
                                                <IconButton size="small" onClick={() => handleDeleteEvent(ev.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedDay(null)} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Add manual event dialog */}
            <Dialog open={eventDialog.open} onClose={() => setEventDialog({ open: false, form: { ...EMPTY_EVENT } })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Cash Flow Event</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Date"
                            type="date"
                            value={eventDialog.form.event_date}
                            onChange={(e) => setEventDialog({ ...eventDialog, form: { ...eventDialog.form, event_date: e.target.value } })}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            select
                            label="Direction"
                            value={eventDialog.form.direction}
                            onChange={(e) => setEventDialog({ ...eventDialog, form: { ...eventDialog.form, direction: e.target.value } })}
                            fullWidth
                        >
                            <MenuItem value="INFLOW">Inflow (money in)</MenuItem>
                            <MenuItem value="OUTFLOW">Outflow (money out)</MenuItem>
                        </TextField>
                        <TextField
                            label="Amount"
                            type="number"
                            value={eventDialog.form.amount}
                            onChange={(e) => setEventDialog({ ...eventDialog, form: { ...eventDialog.form, amount: e.target.value } })}
                            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={eventDialog.form.description}
                            onChange={(e) => setEventDialog({ ...eventDialog, form: { ...eventDialog.form, description: e.target.value } })}
                            fullWidth
                        />
                        <TextField
                            label="Counterparty"
                            value={eventDialog.form.counterparty}
                            onChange={(e) => setEventDialog({ ...eventDialog, form: { ...eventDialog.form, counterparty: e.target.value } })}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEventDialog({ open: false, form: { ...EMPTY_EVENT } })} sx={{ fontFamily, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveEvent}
                        disabled={!eventDialog.form.event_date || !eventDialog.form.amount}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                    >
                        Save Event
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
